<template>
  <div v-if="configFields" class="profile-info-details study-info-container">
    <div class="row">
      <div class="col-md-10 col text-left p-0">
        <p v-translate:profile_information="lang" class="m-0 prof-info-title">
          Profile Information
        </p>
      </div>
    </div>
    <div id="profile" class="profile-info-section">
      <form id="profile-tab" class="profile-info profile-info-page1">
        <!-- username start -->
        <div v-if="pwebCognitoAuthentication">
          <div class="form-group">
            <input
              type="text"
              name="username"
              class="form-control input-lg"
              autocomplete="false"
              v-model="participantRegistration.cognitoUsername"
              @keyup="isValidUsername(participantRegistration.cognitoUsername)"
              @change="isValidUsername(participantRegistration.cognitoUsername)"
              :class="{
                input: true,
                'is-danger': errors.has('username'),
                'is-success': !errors.has('username')
              }"
              required
              v-validate="(getFieldObject('userName') && getFieldObject('userName').isOptional == 'required') || pwebCognitoAuthentication ? 'required' : ''"
            />
            <label v-translate:label_username="lang">Username</label>
            <span v-show="showUsernameError" class="error-style" v-text="'Username is required. Please enter a valid username.'"></span>
          </div>
          <!-- username validation -->
          <div class="regex-req">
            <ul class="validation-list">
              <li>
                <span
                  v-if="usernameValidation.isValidLength !== null"
                  :class="{
                    success: usernameValidation.isValidLength,
                    error: !usernameValidation.isValidLength
                  }"
                  aria-hidden="true"
                ></span>
                <span v-else class="initial-state" aria-hidden="true"></span>
                <span class="form-check-label" v-translate:password_minchar="lang">min. 8 characters</span>
              </li>
              <li>
                <span
                  v-if="usernameValidation.isValidSpecial !== null"
                  :class="{
                    success: usernameValidation.isValidSpecial,
                    error: !usernameValidation.isValidSpecial
                  }"
                  aria-hidden="true"
                ></span>
                <span v-else class="initial-state" aria-hidden="true"></span>
                <span class="form-check-label" v-translate:label_cannot_special_character="lang">cannot begin or end with a special character (.-_@)</span>
              </li>
              <li>
                <span
                  v-if="usernameValidation.isValidUnique !== null"
                  :class="{
                    success: usernameValidation.isValidUnique,
                    error: !usernameValidation.isValidUnique
                  }"
                  aria-hidden="true"
                ></span>
                <span v-else class="initial-state" aria-hidden="true"></span>
                <span class="form-check-label" v-translate:label_must_be_unique="lang">must be unique</span>
              </li>
            </ul>
          </div>
        </div>
        <!-- username end -->

        <!--  password start -->
        <div v-if="!isPhoneVerification || pwebCognitoAuthentication">
          <div class="form-group">
            <input
              type="password"
              name="password"
              ref="password"
              autocomplete="false"
              class="form-control input-lg"
              v-model="participantRegistration.password"
              @keyup="validPassword(participantRegistration.password)"
              :class="{
                input: true,
                'is-danger': errors.has('password'),
                'is-success': !errors.has('password')
              }"
              required
              v-validate="isPhoneVerification ? '' : 'required'"
            />
            <label v-translate:hint_password_signin="lang">Password</label>
            <span v-show="!validPasswordEntered" class="error-style" v-translate:password_requirements="lang">
              Password does not meet complexity requirements
            </span>
          </div>
          <!--  password validation -->
          <div class="regex-req">
            <ul class="validation-list">
              <li>
                <span
                  v-if="passwordReq.showErrors"
                  :class="{
                    success: passwordReq.min8char,
                    error: !passwordReq.min8char
                  }"
                  aria-hidden="true"
                ></span>
                <span v-else class="initial-state" aria-hidden="true"></span>
                <span v-translate:password_minchar="lang">min. 8 characters</span>
              </li>
              <li>
                <span
                  v-if="passwordReq.showErrors"
                  :class="{
                    success: passwordReq.anyLetter,
                    error: !passwordReq.anyLetter
                  }"
                  aria-hidden="true"
                ></span>
                <span v-else class="initial-state" aria-hidden="true"></span>
                <span v-translate:pass_requirment_min_one_letter="lang">at least 1 letter</span>
              </li>
              <li>
                <span
                  v-if="passwordReq.showErrors"
                  :class="{
                    success: passwordReq.number,
                    error: !passwordReq.number
                  }"
                  aria-hidden="true"
                ></span>
                <span v-else class="initial-state" aria-hidden="true"></span>
                <span v-translate:password_onenumber="lang">at least 1 number (12345)</span>
              </li>
              <li>
                <span
                  v-if="passwordReq.showErrors"
                  :class="{
                    success: passwordReq.specialChar,
                    error: !passwordReq.specialChar
                  }"
                  aria-hidden="true"
                ></span>
                <span v-else class="initial-state" aria-hidden="true"></span>
                <span v-translate:PASS_REQUIREMENT_MIN_ONE_SPECIAL_CHAR="lang">at least 1 symbol (!@#$%&)</span>
              </li>
            </ul>
          </div>
          <div class="form-group Re-enter_Pswrd">
            <input
              type="password"
              name="confirmPassword"
              class="form-control input-lg"
              autocomplete="false"
              v-model="participantRegistration.confirmPassword"
              :class="{
                input: true,
                'is-danger': errors.has('confirmPassword'),
                'is-success': !errors.has('confirmPassword')
              }"
              required
              @keyup="checkIfPasswordMatches"
              v-validate="isPhoneVerification ? '' : 'required'"
            />
            <label v-translate:CONFIRM_PASSWORD_TITLE="lang">Confirm Password</label>
            <span v-if="passwordDonotMatches" class="error-style" v-translate:CONFIRM_PSWRD_ENTRY_MUST_MATCH_PSWRD="lang">
              Confirm Password entry must match the password.
            </span>
          </div>
        </div>
        <!-- password end -->

        <!-- email start -->
        <div class="form-group" v-if="!isPhoneVerification && getFieldObject('email') && getFieldObject('email').isVisible">
          <input
            type="email"
            name="email"
            class="form-control input-lg disabled-bg"
            v-model="participantRegistration.email"
            :class="{
              input: true,
              'is-danger': errors.has('email'),
              'is-success': !errors.has('email')
            }"
            required
            :disabled="!portal.isOpenEnrollmentStudy()"
            v-validate="getFieldObject('email').isOptional == 'required' ? 'required' : ''"
          />
          <label
            :class="{
              'change-email-open-enrollment': participantRegistration.email
            }"
            v-translate:email="lang"
            >Email</label
          >
          <span v-show="errors.has('email')" class="error-style" v-translate:error_mandatory_field="lang">
            Mandatory field
          </span>
        </div>
        <!-- email end -->

        <!-- TUR-61129 Identity field -->
        <!-- phone number start -->
        <div class="phone-number-wrap mt-24" v-else>
          <h5 v-translate:label_verification_phone="lang">
            Verification Phone
          </h5>
          <p v-if="pwebCognitoAuthentication" v-translate:label_registration_verification_phone_cognito_desc="lang">
            This phone number will be used to text and verify your account.
          </p>
          <p v-else v-translate:label_registration_verification_phone_desc="lang">
            This phone number will be used to text and verify your account every time you sign in.
          </p>
          <div class="row">
            <vue-tel-input
              class="mb-0"
              wrapperClasses="vue-tel-macho-style"
              type="tel-input"
              name="mobile"
              mode="national"
              enabledCountryCode
              disabled
              @validate="obj => (phoneObject = obj)"
              v-vue-tel-label="widget.translate('verification_phone_number_mobile', 'Verification Phone Number (Mobile)')"
              :value="`${participantRegistration.mobileCountryCode}${participantRegistration.mobile}`"
              :preferredCountries="['us']"
              :class="{
                input: true,
                'is-danger': errors.has('mobile'),
                'is-success': !errors.has('mobile')
              }"
            >
            </vue-tel-input>
          </div>
          <div class="form-check pt-19">
            <input class="form-check-input" v-model="participantRegistration.verificationOptInSMS" type="checkbox" value="" id="defaultCheck1" />
            <span
              class="
                  form-check-label form-check-phone
                  d-block
                  ml-11
                  cursor-contextMenu
                "
              for="defaultCheck1"
              v-translate:label_sms_checkbox="lang"
            >
              Receive SMS notifications about this study (standard messaging rates may apply). Opt in/out from Profile settings.
            </span>
          </div>
        </div>
        <!-- phone number end -->
        <!-- TUR-61129 Identity field -->
        <!-- parent/ caregiver first name start-->
        <div class="form-group" v-if="getFieldObject('parentOrCgFirstName') && getFieldObject('parentOrCgFirstName').isVisible">
          <input
            type="text"
            class="form-control input-lg"
            name="Cfname"
            v-model="participantRegistration.participantMeta.parentOrCgFirstName"
            :class="{
              input: true,
              'is-danger': errors.has('Cfname'),
              'is-success': !errors.has('Cfname')
            }"
            required
            onkeypress="return /[a-zA-Z\s]/i.test(event.key)"
            v-validate="getFieldObject('parentOrCgFirstName') && getFieldObject('parentOrCgFirstName').isOptional === 'required' ? 'required' : ''"
          />
          <label v-translate:participant_caregiver_first_name="lang">Parent/Caregiver First Name</label>
          <span v-show="errors.has('Cfname')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
        </div>
        <!-- parent/ caregiver first name end-->

        <!-- parent/ caregiver last name start-->
        <div class="form-group" v-if="getFieldObject('parentOrCgLastName') && getFieldObject('parentOrCgLastName').isVisible">
          <input
            type="text"
            class="form-control input-lg"
            name="Clname"
            v-model="participantRegistration.participantMeta.parentOrCgLastName"
            :class="{
              input: true,
              'is-danger': errors.has('Clname'),
              'is-success': !errors.has('Clname')
            }"
            required
            onkeypress="return /[a-zA-Z\s]/i.test(event.key)"
            v-validate="getFieldObject('parentOrCgLastName') && getFieldObject('parentOrCgLastName').isOptional == 'required' ? 'required' : ''"
          />
          <label v-translate:participant_caregiver_last_name="lang">Parent/Caregiver Last Name</label>
          <span v-show="errors.has('Clname')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
        </div>
        <!-- parent/ caregiver last name end-->
        <!-- first name start -->
        <div class="form-group" v-if="getFieldObject('firstName') && getFieldObject('firstName').isVisible">
          <input
            type="text"
            class="form-control input-lg"
            onkeypress="return /[a-zA-Z\s]/i.test(event.key)"
            v-model="participantRegistration.participantMeta.firstName"
            name="fname"
            :class="{
              input: true,
              'is-danger': errors.has('fname'),
              'is-success': !errors.has('fname')
            }"
            required
            v-validate="getFieldObject('firstName') && getFieldObject('firstName').isOptional == 'required' ? 'required' : ''"
          />
          <label v-translate:CONSENT_NAME_GIVEN="lang">First Name</label>
          <span v-show="errors.has('fname')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
        </div>
        <!-- first name end -->

        <!-- last name start -->
        <div class="form-group" v-if="getFieldObject('lastName') && getFieldObject('lastName').isVisible">
          <input
            type="text"
            class="form-control input-lg"
            onkeypress="return /[a-zA-Z\s]/i.test(event.key)"
            v-model="participantRegistration.participantMeta.lastName"
            name="lname"
            :class="{
              input: true,
              'is-danger': errors.has('lname'),
              'is-success': !errors.has('lname')
            }"
            required
            v-validate="getFieldObject('lastName') && getFieldObject('lastName').isOptional == 'required' ? 'required' : ''"
          />
          <label v-translate:CONSENT_NAME_FAMILY="lang">Last Name</label>
          <span v-show="errors.has('lname')" class="error-style" v-translate:error_mandatory_field="lang">
            Mandatory field
          </span>
        </div>
        <!-- last name end -->

        <!-- DOB start -->
        <div v-clickoutside="handleFocusOut" class="form-group form-align-calender" v-if="getFieldObject('birthDate') && getFieldObject('birthDate').isVisible">
          <datepicker
            :minimum-view="calenderMinimumView"
            input-class="inner-card-value dob-background"
            :class="{
              'form-control': true,
              'is-danger': errors.has('dob'),
              'is-success': !errors.has('dob')
            }"
            ref="DatePicker"
            :format="dateFormat"
            :placeholder="dobPlaceholderValue"
            v-model="participantRegistration.participantMeta.dob"
            name="dob"
            :disabled-dates="disabledDates"
            @focusin.native="handleFocusOnDatePicker"
            @closed="closedDatePicker"
            :language="calenderLang"
            required
            v-validate="getFieldObject('birthDate') && getFieldObject('birthDate').isOptional === 'required' ? 'required' : ''"
          />
          <div class="calenderIcon">
            <span>
              <i class="fa fa-calendar-o" aria-hidden="true" @click="openDatePicker"></i>
            </span>
            <label v-if="showDobLabel" v-translate:birth_date="lang" v-translate:birthdate_key="lang">Birth date</label>
          </div>
          <span v-if="errors.has('dob')" class="error-style" v-translate:error_mandatory_field="lang">
            Mandatory field
          </span>
        </div>
        <!-- DOB end -->

        <!-- Age range start -->
        <div class="form-group floating-label" v-if="getFieldObject('ageRange') && getFieldObject('ageRange').isVisible">
          <select
            class="form-control type_select option-width floating-select"
            name="ageRange"
            id="ageRange"
            v-model="participantRegistration.participantMeta.ageRange"
            :class="{
              input: true,
              'is-danger': errors.has('ageRange'),
              'is-success': !errors.has('ageRange')
            }"
            @change="setValueAttribute('ageRange', participantRegistration.participantMeta.ageRange)"
            value=""
            v-validate="getFieldObject('ageRange').isOptional == 'required' ? 'required' : ''"
          >
            <option v-for="value in ageRangeOptions" :value="value" :key="value">
              {{ value }}
            </option>
          </select>
          <label class="label" v-translate:participant_age_range="lang">Age Range</label>
          <span v-show="errors.has('ageRange')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
        </div>
        <!-- Age range end -->

        <!-- Gender start -->
        <div class="form-group floating-label" v-if="getFieldObject('gender') && getFieldObject('gender').isVisible">
          <select
            class="form-control type_select option-width floating-select"
            name="gender"
            id="gender"
            :class="{
              input: true,
              'is-danger': errors.has('gender'),
              'is-success': !errors.has('gender')
            }"
            v-model="participantRegistration.participantMeta.gender"
            @change="onGenderSelected('gender', participantRegistration.participantMeta.gender)"
            value=""
            v-validate="getFieldObject('gender') && getFieldObject('gender').isOptional == 'required' ? 'required' : ''"
          >
            <option
              v-for="gender in getFieldObject('gender').expectedValues"
              :value="gender"
              :key="gender"
              :hidden="gender === otherGender"
              v-translate:dynamic_string="{
                key: getGenderType(gender),
                val: gender,
                html: gender
              }"
            >
              {{ gender }}
            </option>
          </select>
          <label class="label" v-translate:label_gender="lang">Gender</label>
          <span class="error-style" v-translate:error_mandatory_field="lang" v-if="errors.has('gender')">Mandatory field</span>
        </div>
        <!-- Gender end -->

        <!-- Language start -->
        <div class="form-group" v-if="getFieldObject('language') && getFieldObject('language').isVisible">
          <input
            type="text"
            class="form-control input-lg"
            v-model="participantRegistration.participantMeta.language"
            onkeypress="return /[a-zA-Z\s]/i.test(event.key)"
            name="language"
            :class="{
              input: true,
              'is-danger': errors.has('language'),
              'is-success': !errors.has('language')
            }"
            required
            v-validate="getFieldObject('language') && getFieldObject('language').isOptional == 'required' ? 'required' : ''"
          />
          <label v-translate:participant_primary_lang="lang">Primary Language</label>
          <span v-show="errors.has('language')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
        </div>
        <!-- Language start -->

        <!-- Custom fields start -->
        <div v-if="this.customRegistrationField">
          <div v-for="customField in this.customRegistrationField" :key="customField.key">
            <div v-if="customField.expectedCountries && customField.expectedCountries.includes(portal.getCountry())">
              <div class="form-group" v-if="getFieldObject(customField.key) && getFieldObject(customField.key).isVisible">
                <input
                  type="text"
                  class="form-control input-lg"
                  v-model="participantRegistration.participantMeta[customField.key]"
                  name="regCustomField1"
                  :class="{
                    input: true,
                    'is-danger': errors.has(customField.key),
                    'is-success': !errors.has(customField.key)
                  }"
                  required
                  v-validate="getFieldObject(customField.key) && getFieldObject(customField.key).isOptional == 'required' ? 'required' : ''"
                />
                <label>{{ customField.field }}</label>
                <span v-show="errors.has(customField.key)" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Custom fields end -->
      </form>
    </div>
    <div class="">
      <!-- Contact info start-->
      <div class="col-md-10 col text-left p-0 pt-48">
        <h5 v-if="isContactInformationVisible()" v-translate:label_contact_details="lang" class="m-0 cursor-contextMenu">
          Contact Information
        </h5>
      </div>
      <div id="profile" class="profile-info-section">
        <form id="profile-tab" class="profile-info">
          <!-- Address start -->
          <div class="form-group" v-if="getFieldObject('address') && getFieldObject('address').isVisible">
            <input
              type="text"
              class="form-control input-lg"
              v-model="participantRegistration.participantMeta.address"
              name="address"
              :class="{
                input: true,
                'is-danger': errors.has('address'),
                'is-success': !errors.has('address')
              }"
              required
              v-validate="getFieldObject('address') && getFieldObject('address').isOptional == 'required' ? 'required' : ''"
            />
            <label v-translate:participant_address="lang">Address</label>
            <span v-show="errors.has('address')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
          </div>
          <div class="form-group" v-if="getFieldObject('city') && getFieldObject('city').isVisible">
            <input
              type="text"
              class="form-control input-lg"
              v-model="participantRegistration.participantMeta.city"
              name="city"
              :class="{
                input: true,
                'is-danger': errors.has('city'),
                'is-success': !errors.has('city')
              }"
              required
              v-validate="getFieldObject('city').isOptional == 'required' ? 'required' : ''"
            />
            <label v-translate:participant_city_town="lang">City</label>
            <span v-show="errors.has('city')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
          </div>

          <div class="form-group" v-if="getFieldObject('stateProvince') && getFieldObject('stateProvince').isVisible">
            <input
              type="text"
              class="form-control input-lg"
              v-model="participantRegistration.participantMeta.state"
              name="stateProvince"
              :class="{
                input: true,
                'is-danger': errors.has('stateProvince'),
                'is-success': !errors.has('stateProvince')
              }"
              required
              v-validate="getFieldObject('stateProvince') && getFieldObject('stateProvince').isOptional == 'required' ? 'required' : ''"
            />
            <label v-translate:participant_state_region="lang">{{ getFieldObject("stateProvince").field }}</label>
            <span v-show="errors.has('stateProvince')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
          </div>

          <div class="form-group" v-if="getFieldObject('zipCode') && getFieldObject('zipCode').isVisible">
            <input
              type="number"
              class="form-control input-lg"
              v-model="participantRegistration.participantMeta.zipcode"
              @keypress="numberOnly"
              name="zipcode"
              :class="{
                input: true,
                'is-danger': errors.has('zipCode'),
                'is-success': !errors.has('zipCode')
              }"
              required
              v-validate="getFieldObject('zipCode').isOptional == 'required' ? 'required' : ''"
            />
            <label v-translate:participant_zip_postal="lang">Zip/Postal Code</label>
            <span v-show="errors.has('zipcode')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
          </div>

          <div class="form-group" v-if="getFieldObject('country') && getFieldObject('country').isVisible">
            <input
              type="text"
              class="form-control input-lg"
              onkeypress="return /[a-zA-Z\s]/i.test(event.key)"
              v-model="participantRegistration.participantMeta.country"
              name="country"
              :class="{
                input: true,
                'is-danger': errors.has('country'),
                'is-success': !errors.has('country')
              }"
              required
              v-validate="getFieldObject('country') && getFieldObject('country').isOptional == 'required' ? 'required' : ''"
            />
            <label v-translate:country_title="lang">{{ getFieldObject("country").field }}</label>
            <span v-show="errors.has('country')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
          </div>
          <!-- Address end -->
          <!-- Contact number start -->
          <div class="form-group" v-if="getFieldObject('nonMobilePhoneNumber') && getFieldObject('nonMobilePhoneNumber').isVisible">
            <input
              type="number"
              class="form-control input-lg"
              v-model="participantRegistration.participantMeta.nonMobilePhoneNumber"
              @keypress="numberOnly"
              name="nonMobilePhoneNumber"
              :class="{
                input: true,
                'is-danger': errors.has('nonMobilePhoneNumber'),
                'is-success': !errors.has('nonMobilePhoneNumber')
              }"
              required
              v-validate="getFieldObject('nonMobilePhoneNumber').isOptional == 'required' ? 'required' : ''"
            />
            <label v-translate:participant_non_mob_phone="lang">{{ getFieldObject("nonMobilePhoneNumber").field }}</label>
            <span v-show="errors.has('nonMobilePhoneNumber')" class="error-style" v-translate:error_mandatory_field="lang">
              Phone number is required.
            </span>
          </div>
          <div class="phone-number-wrap mt-24" v-if="getFieldObject('phoneNumber') && getFieldObject('phoneNumber').isVisible">
            <template v-if="isPhoneVerification">
              <h5 class="mb-0" v-translate:label_phone="lang">Phone</h5>
              <p v-translate:label_registration_phone_desc="lang">
                This phone number is where you will get text messages for everything in the study.
              </p>
              <div class="form-check pb-19">
                <input class="form-check-input" v-model="isSameAsVerificationPhone" type="checkbox" value="" id="defaultCheck1" @change="makeSameAsVerificationPhone" />
                <span class="form-check-label form-check-phone d-block ml-11 cursor-contextMenu" for="defaultCheck1" v-translate:label_same_as_verification_phone="lang">
                  Same as Verification Phone Number
                </span>
              </div>
            </template>
            <div class="row pb-19" v-if="isContactPhoneNumberVisible">
              <vue-tel-input
                class="mb-0"
                type="tel-input"
                name="phoneNumber"
                wrapperClasses="vue-tel-macho-style"
                @onInput="onInpuPhoneNumber"
                @country-changed="countryChanged"
                :dropdownOptions="{ showDialCodeInSelection: true }"
                mode="national"
                validCharactersOnly
                enabledCountryCode
                v-vue-tel-label="widget.translate('label_enter_phone_number', 'Enter your phone number')"
                :preferredCountries="['us']"
                :defaultCountry="phoneObject && phoneObject.country ? phoneObject.country.iso2 : defaultCountry"
                :class="{
                  input: true,
                  'is-danger': errors.has('phoneNumber'),
                  'is-success': !errors.has('phoneNumber')
                }"
              >
              </vue-tel-input>
              <span v-if="formValidated && errors.has('phoneNumber') && contactPhoneObject && contactPhoneObject.number.input == ''" class="error-style phone-error-style" v-translate:error_mandatory_field="lang">Phone number is required.</span>
              <span v-else-if="!validTelNumber" class="error-style phone-error-style" v-translate:label_enter_valid_phone="lang">Please enter a valid phone number.</span>
            </div>
            <div class="form-check" v-if="isContactPhoneNumberVisible">
              <input :disabled="contactPhoneObject ? (contactPhoneObject.number.significant != '' && validTelNumber ? false : true) : true" class="form-check-input" v-model="participantRegistration.participantMeta.optInSMS" type="checkbox" value="" id="defaultCheck1" />
              <span
                class="
                  form-check-label form-check-phone
                  d-block
                  ml-11
                  cursor-contextMenu
                "
                for="defaultCheck1"
                v-translate:label_sms_checkbox="lang"
              >
                Receive SMS notifications about this study (standard messaging rates may apply). Opt in/out from Profile settings.
              </span>
            </div>
            <div class="form-group" v-if="getFieldObject('howDidYouHear') && getFieldObject('howDidYouHear').isVisible">
              <select
                class="form-control type_select option-width floating-select"
                name="howDidYou"
                id="howDidYou"
                v-model="participantRegistration.participantMeta.howDidYouHear"
                :class="{
                  input: true,
                  'is-danger': errors.has('howDidYou'),
                  'is-success': !errors.has('howDidYou')
                }"
                @change="setValueAttribute('howDidYou', participantRegistration.participantMeta.howDidYouHear)"
                value=""
                v-validate="getFieldObject('howDidYouHear') && getFieldObject('howDidYouHear').isOptional == 'required' ? 'required' : ''"
              >
                <option
                  v-for="(value, i) in howDidYouHear"
                  :value="value"
                  :key="value"
                  v-translate:dynamic_string="{
                    key: `hear_about_values_${i + 1}`,
                    val: value,
                    html: value
                  }"
                >
                  {{ value }}
                </option>
              </select>
              <label class="label" v-translate:how_did_hear_us="lang">How did you hear about us?</label>
              <span v-show="errors.has('howDidYou')" class="error-style" v-translate:error_mandatory_field="lang">Mandatory field</span>
            </div>
          </div>
          <!-- Contact number end -->
        </form>
      </div>
      <div class="col-md-1 col"></div>
    </div>
    <div class="text-center pt-45">
      <button :disabled="disableNextBtn" class="btn btn-submit primary-bg" v-translate:text_signin_button="lang" data-toggle="modal" data-target="#ConfirmEmailModal" data-dismiss="modal" @click.prevent="saveParticipant">
        Next
      </button>
    </div>
    <VerifyIdentityModal v-model="verifyIdentity" :lang="lang" :date="date" :phoneObject="phoneObject" @onChangeEmail="onChangeEmail" :participantId="participantId" />
    <!-- Modal for gender -->
    <b-modal size="lg" :body-class="['p-0']" class="export-activity-success-modal d-header-none" @hide="onCloseModel" scrollable :no-close-on-backdrop="true" style="width: 538px" v-model="isOtherGender">
      <div class="participant-gender-modal">
        <img alt="add" src="static/img/add.svg" class="close-icon ml-auto pr-16 pt-16" style="float: right" @click="onCloseGenderModel" />
        <div class="text-center p-48">
          <div class="form-group pt-80">
            <input type="text" required="required" v-model="otherGender" class="form-control" />
            <label v-translate:ENTER_GENDER_PREFERENCE="lang">Enter gender preference</label>
          </div>

          <div class="text-center pt-45 pb-48">
            <button class="btn btn-submit" @click="onCloseGenderModel" v-translate:PREFER_NOT_TO_SAY_TEXT="lang">
              I prefer not to say
            </button>
            <button class="btn btn-submit primary-bg mt-25" :disabled="otherGender === ''" style="background-color: var(--primary) !important" @click="onGenderChange" v-translate:rsb_done="lang">
              Done
            </button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Modal for Change email -->
    <b-modal size="lg" :body-class="['p-0']" class="export-activity-success-modal d-header-none" @hide="isChangeEmail = false" scrollable :no-close-on-backdrop="true" style="width: 538px" v-model="isChangeEmail">
      <div class="participant-gender-modal reg-change-email-modal">
        <img alt="add" src="static/img/add.svg" class="close-icon ml-auto pr-16 pt-16" style="float: right" @click="isChangeEmail = false" />
        <div class="partipant-registration-modal__container">
          <div class="confirmEmailTitle pt-48 cursor-contextMenu" v-translate:label_email_change="lang">
            Change Email
          </div>
          <div class="text-center p-48 reg-modal-change-email">
            <input
              type="email"
              name="email"
              class="form-control input-lg disabled-bg"
              v-translate:enter_new_email.placeholder="lang"
              placeholder="Enter new email"
              v-model="newEmailAddress"
              :class="{
                input: true,
                'is-danger': errors.has('email'),
                'is-success': !errors.has('email')
              }"
              required
            />
            <label>Change Email</label>
          </div>
          <div class="text-center p-45">
            <button class="btn btn-submit primary-bg" :disabled="!newEmailAddress" v-translate:rsb_done="lang" @click="saveParticipant">
              Done
            </button>
          </div>
        </div>
      </div>
    </b-modal>
    <Modal :show="showOptInSmsModal" :cancelButton="modal.cancelBtnTitle" :title="modal.title" :helpText="modal.helpText" :okButton="modal.okBtnTitle" @modalCallback="modalCallback" />
  </div>
  <div v-else>
    <loader></loader>
    <vue-progress-bar class="progress_bar_header"></vue-progress-bar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Datepicker from "vuejs-datepicker";
import * as calenderLanguage from "vuejs-datepicker/dist/locale";
import { Consent, Bitrise, Widget, ParticipantPortal, Participant, Validations, DatavantTokens } from "@/components/services/participant-service";
import countriesDataLists from "@/data/countries-data.json";
import { v4 as uuidv4 } from "uuid";
import loader from "@/components/common/loader";
import moment from "moment";
import localStorageService from "@/services/localStorage";
import VerifyIdentityModal from "@/components/participant-portal/auth/join/verifyOtpModal";
import Modal from "@/components/participant-portal/common/modal";
import _ from "lodash";
import nprogress from "nprogress";
import swal from "sweetalert2";

const date = new Date();
export default {
  name: "registration",
  components: { loader, VerifyIdentityModal, Modal, Datepicker },
  props: ["lang", "selectedCohort", "consentSignatureAndDisclaimer", "isDatavantTokenizationEnabled"],
  data() {
    return {
      passwordReq: {
        min8char: false,
        anyLetter: false,
        number: false,
        specialChar: false,
        passwordReusedCheck: false,
        showErrors: false,
        passwordMatched: false,
        validated: false
      },
      usernameValidation: {
        isValidUnique: null,
        isValidSpecial: null,
        isValidLength: null
      },
      usernameValidator: {
        minLength: 8,
        minLetter: 0,
        minNumber: 0,
        minSpecial: 0,
        regSpecial: new RegExp(/^[\p{L}\p{N}][\p{L}\p{N}.@_-]*[\p{L}\p{N}]$/, "gu"),
        regLetter: new RegExp(/\p{L}/, "gu"),
        regNumber: new RegExp(/\p{N}/, "gu")
      },
      showOptInSmsModal: false,
      modal: {
        title: "",
        helpText: "",
        okBtnTitle: "",
        cancelBtnTitle: ""
      },
      passwordNotMatched: false,
      portal: new ParticipantPortal(),
      errorMsg: null,
      pid: "",
      bitrise: new Bitrise(),
      participantDetails: new Participant(),
      consent: new Consent(),
      validate: new Validations(),
      defaultCountry: "us",
      show: false,
      configFields: null,
      globalElement: null,
      hasFieldError: false,
      verifyIdentity: false,
      date: new Date(),
      phoneObject: null,
      contactPhoneObject: null,
      howDidYouHear: [
        // remaining
        "Facebook",
        "Twitter",
        "Snapchat",
        "Other Social Media",
        "Blog",
        "Google Search",
        "Apple App Store",
        "Google Play Store"
      ],
      ageRangeOptions: [
        // remaining
        "Under 18",
        "18-29",
        "30-49",
        "50-64",
        "65+"
      ],
      widget: new Widget(),
      confirmEmailModal: false,
      registrationFields: [],
      customRegistrationField: [],
      cohartId: "",
      getCountriesLists: [],
      showLoader: false,
      isVerifyEmailConfim: false,
      validPhoneNumber: false,
      formValidated: false,
      isOtherGender: false,
      otherGender: "",
      coreGenders: [],
      validated: false,
      accesstoken: "",
      isChangeEmail: false,
      newEmailAddress: "",
      isSameAsVerificationPhone: false,
      participantId: "",
      disableNextBtn: false,
      datavantTokens: new DatavantTokens(),
      datavantTokenFields: null,
      associatedConsent: null,
      dobInputFormat: "birthDate",
      calenderMinimumView: "",
      disabledDates: {
        from: new Date()
      },
      dobPlaceholder: "",
      dobPlaceholderValue: "",
      showDobLabel: true,
      calenderLang: calenderLanguage.en
    };
  },
  mounted() {
    moment.locale("en");
    this.defaultCountry = this.portal.getDefaultCountryIso2();
    this.getFormConfig();
    this.$emit("updateHeaderNav", {
      showBlankHeader: true,
      title: this.widget.translate("rsb_registration_step_title", "Registration"),
      hideBack: true
    });
    this.$root.$emit("SetAfterCancelClosedScreen", "joinIntro");
    this.participantRegistration.email = this.participantDetails.getParticipantEmail();
    this.getListOfcohart();
    this.getCountriesList();
    this.participantRegistration.cognitoUsername = "";
    this.participantRegistration.password = "";
    this.participantRegistration.confirmPassword = "";
    Object.keys(this.participantRegistration.participantMeta).forEach(key => {
      if (key === "optInSMS") {
        this.participantRegistration.participantMeta.optInSMS = false;
        return;
      } else if (key !== "firstName" && key !== "lastName") {
        this.participantRegistration.participantMeta[key] = "";
      }
    });
  },
  beforeDestroy() {
    moment.locale(this.lang);
  },
  computed: {
    ...mapGetters(["participant", "participantRegistration", "token", "pwebCognitoAuthentication"]),
    study() {
      return this.$store.getters.getParticipantsPortalStudyData;
    },
    dateFormat() {
      return this.getFormats();
    },
    isPhoneNumberRequired() {
      return this.getFieldObject("phoneNumber").isVisible ? !!(this.getFieldObject("phoneNumber") && this.getFieldObject("phoneNumber").isOptional == "required") : false;
    },
    isUsernameValid() {
      return !!(this.usernameValidation.isValidLength && this.usernameValidation.isValidSpecial && this.usernameValidation.isValidUnique);
    },
    showUsernameError() {
      return (this.validated && !this.isUsernameValid) || (this.participantRegistration.cognitoUsername !== "" && !this.isUsernameValid);
    },
    validPasswordEntered() {
      return (!this.passwordReq.showErrors && !this.passwordReq.validated) || (this.passwordReq.showErrors && this.passwordReq.min8char && this.passwordReq.anyLetter && this.passwordReq.number && this.passwordReq.specialChar);
    },
    confirmPasswordErrors() {
      return this.errors.firstByRule("confirmPassword", "required");
    },
    passwordMandatory() {
      return this.veeFields.confirmPassword && this.errors.has("confirmPassword") && this.errors.firstByRule("confirmPassword", "required");
    },
    passwordDonotMatches() {
      if (this.formValidated && !this.passwordReq.passwordMatched) {
        return true;
      } else if (!this.passwordReq.passwordMatched && this.participantRegistration.confirmPassword !== "") {
        return true;
      } else {
        return false;
      }
    },
    maxDate() {
      const now = new Date();
      return now.toISOString().substring(0, 10);
    },
    participantName() {
      return this.participantRegistration.participantMeta.firstName + " " + this.participantRegistration.participantMeta.lastName;
    },
    isPhoneVerification() {
      return this.participantDetails.isPhoneVerification;
    },
    isContactPhoneNumberVisible() {
      return this.isPhoneVerification ? !this.isSameAsVerificationPhone : true;
    },
    validTelNumber() {
      if (this.isPhoneVerification && this.isSameAsVerificationPhone) {
        return true;
      } else if (this.isPhoneNumberRequired) {
        if (this.contactPhoneObject) {
          return this.contactPhoneObject.valid;
        } else if (this.formValidated) {
          return false;
        } else {
          return true;
        }
      } else {
        if (this.contactPhoneObject && this.contactPhoneObject.number.input.trim() != "") {
          return this.contactPhoneObject.valid;
        } else {
          return true;
        }
      }
    }
  },
  watch: {
    newEmailAddress() {
      this.participantRegistration.email = this.newEmailAddress;
    },
    participant() {
      this.showLoader = true;
      if (!this.portal.isManualStudyStart()) {
        this.confirmParticipant();
      }
      localStorageService.setItem("pw-languageId", this.portal.getLanguage());
      localStorageService.setItem("pw-deviceId", uuidv4());
      this.portal
        .getBitriseData()
        .then(
          () => {
            this.portal.getGlobalElementsObject();
            this.$emit("updateHeaderNav", { showBlankHeader: false });
            this.showLoader = false;
            this.$router.push({
              name: "participants-portal-activities",
              params: {
                studyId: this.portal.getStudy(),
                languageId: this.portal.getLanguage()
              }
            });
          },
          error => {
            const message = this.widget.translate(error.message, "Data sync failed, please try again after some time.");
            if (message) {
              this.$toastr.error(message);
            }
          }
        )
        .catch(error => {
          const message = this.widget.translate(error.message, "Data sync failed, please try again after some time.");
          if (message) {
            this.$toastr.error(message);
          }
        });
    }
  },
  methods: {
    ...mapActions(["saveParticipantRegistration", "awsCognitoParticipantLogin"]),
    preventUpperCase(evt) {
      if (evt && /^[A-Z]*$/.test(evt.key)) {
        evt.preventDefault();
      }
    },
    getFormats() {
      switch (this.dobInputFormat) {
        case "dobWithYear": {
          this.calenderMinimumView = "year";
          this.dobPlaceholder = "yyyy";
          return "yyyy";
        }
        case "dobWithMonthYear": {
          this.calenderMinimumView = "month";
          this.dobPlaceholder = "mm-yyyy";
          return "MMM, yyyy";
        }
        default: {
          this.dobPlaceholder = "mm-dd-yyyy";
          return "MMM dd, yyyy";
        }
      }
    },
    handleFocusOut() {
      if (!this.participantRegistration.participantMeta.dob) {
        this.showDobLabel = true;
        this.dobPlaceholderValue = "";
      }
    },
    handleFocusOnDatePicker() {
      if (!this.participantRegistration.participantMeta.dob) {
        this.showDobLabel = false;
        this.dobPlaceholderValue = this.dobPlaceholder;
      }
    },
    openDatePicker() {
      if (!this.participantRegistration.participantMeta.dob) {
        this.showDobLabel = !this.showDobLabel;
      }
      if (this.showDobLabel) {
        this.dobPlaceholderValue = "";
      }
      if (!this.showDobLabel) {
        this.dobPlaceholderValue = this.dobPlaceholder;
        const _this = this;
        this.$nextTick(function() {
          _this.$refs.DatePicker.showCalendar();
          _this.$refs.DatePicker.$el.querySelector("input").focus();
        });
      }
    },
    closedDatePicker() {
      if (!this.participantRegistration.participantMeta.dob) {
        this.$refs.DatePicker.showCalendar();
      }
    },
    getDobFomatted(dob) {
      let dateObject = {};
      switch (this.calenderMinimumView) {
        case "year": {
          dateObject.dobWithYear = moment(dob).format("YYYY");
          dateObject.dobYear = moment(dob).format("YYYY");
          break;
        }
        case "month": {
          dateObject.dobWithMonthYear = moment(dob).format("YYYY-MM");
          dateObject.dobYear = moment(dob).format("YYYY");
          dateObject.dobMonth = moment(dob).format("MM");
          break;
        }
        default: {
          dateObject.dob = moment(dob).format("YYYY-MM-DD");
          dateObject.dobDay = moment(dob).format("DD");
          dateObject.dobMonth = moment(dob).format("MM");
          dateObject.dobYear = moment(dob).format("YYYY");
          break;
        }
      }
      this.participantRegistration.participantMeta = {
        ...this.participantRegistration.participantMeta,
        ...dateObject
      };
    },
    modalCallback() {
      this.participantRegistration.verificationOptInSMS = true;
    },
    onChangeEmail() {
      this.isChangeEmail = true;
    },
    numberOnly(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57) && (charCode < 96 || charCode > 105)) {
        evt.preventDefault();
      }
    },
    onCloseGenderModel() {
      if (this.coreGenders.length === 0) {
        this.coreGenders = this.getFieldObject("gender").expectedValues;
      }
      this.otherGender = "";
      this.getFieldObject("gender").expectedValues = this.coreGenders;
      this.participantRegistration.participantMeta.gender = "Other gender identity";
      this.isOtherGender = false;
    },
    onGenderChange() {
      if (this.coreGenders.length === 0) {
        this.coreGenders = this.getFieldObject("gender").expectedValues;
      }
      const isExist = this.getFieldObject("gender").expectedValues.indexOf(this.otherGender) > -1;
      if (this.otherGender !== "" && !isExist) {
        this.getFieldObject("gender").expectedValues = [...this.coreGenders, this.otherGender];
        this.participantRegistration.participantMeta.gender = this.otherGender;
      } else {
        this.getFieldObject("gender").expectedValues = this.coreGenders;
      }
      this.isOtherGender = false;
    },
    onGenderSelected(idName, value) {
      this.setValueAttribute("gender", this.participantRegistration.participantMeta.gender);
      this.otherGender = "";
      if (value == "Other gender identity") {
        this.isOtherGender = true;
      } else {
        this.onGenderChange();
      }
    },
    /**
     * setValueAttribute function call triggers the easing effect of placeholder label for select inputs
     */
    setValueAttribute(idName, value) {
      document.getElementById(idName).setAttribute("value", value);
    },
    onInpuPhoneNumber(contactPhoneObject) {
      this.contactPhoneObject = contactPhoneObject;
      this.participantRegistration.participantMeta.countryCode = "+" + contactPhoneObject.country.dialCode;
      const lastEnteredPhone = this.contactPhoneObject && this.contactPhoneObject.number ? "+" + this.contactPhoneObject.country.dialCode + this.contactPhoneObject.number.significant : "";
      const verificationPhone = this.participantRegistration.mobileCountryCode + this.participantRegistration.mobile;
      if (lastEnteredPhone == verificationPhone) {
        this.isSameAsVerificationPhone = true;
      }
    },
    countryChanged(contactPhoneObject) {
      if (contactPhoneObject) {
        this.participantRegistration.participantMeta.countryCode = "+" + contactPhoneObject.dialCode;
      }
    },
    setCountryCodeInsideFlag() {},
    getCountriesList() {
      _.forEach(countriesDataLists, value => {
        const obj = {
          dial_code: "+" + value[2],
          code: value[1].toLowerCase(),
          name: value[0],
          image_icon: this.getFlagIcon(value[1].toLowerCase())
        };
        this.getCountriesLists.push(obj);
      });
    },
    getFlagIcon(key) {
      return this.bitrise.getImage(key);
    },
    getFields() {
      const fieldsObj = this.bitrise.getJsonData("study_participantregistrationconfiguration");
      if (fieldsObj) {
        fieldsObj
          .then(response => {
            this.registrationFields = response.participantRegistrationConfigDetails;
          })
          .catch(e => {
            const message = this.widget.translate(e.message, "Data sync failed, please try again after some time.");
            if (message) {
              this.$toastr.error(message);
            }
          });
      }
    },
    isContactInformationVisible() {
      const contactDetailsfields = ["phoneNumber", "address", "country", "zipCode", "stateProvince", "city"];
      const isFieldRequired = [];
      contactDetailsfields.filter(field => {
        const object = this.configFields.find(o => o.key == field);
        if (object && object.isVisible) {
          isFieldRequired.push(object);
        }
      });
      return !!(isFieldRequired && isFieldRequired.length > 0);
    },
    isValidUsername(username) {
      const that = this;
      username = username ? username.toLowerCase() : username;
      Object.assign(this.usernameValidation, this.validate.validateUsername(username, this.usernameValidator));
      if (username !== "") {
        if (that.usernameValidation.isValidLength && that.usernameValidation.isValidSpecial) {
          this.portal
            .cognitoCheckUsername(username)
            .then(res => {
              that.usernameValidation.isValidUnique = that.participantRegistration.cognitoUsername === "" ? null : !that.usernameValidation.isValidLength && !that.usernameValidation.isValidSpecial ? false : res.errorCode == null;
            })
            .catch(() => {
              that.usernameValidation.isValidUnique = false;
            });
        } else {
          this.usernameValidation.isValidUnique = false;
        }
      } else {
        this.usernameValidation.isValidUnique = null;
      }
    },
    validUsername(username) {
      username = username ? username.toLowerCase() : username;
      this.usernameReq.showErrors = true;
      this.portal
        .cognitoCheckUsername(username)
        .then(res => {
          this.usernameReq.unique = res.errorCode == null;
        })
        .catch(() => {
          const message = this.widget.translate("PARTICIPANT_NOT_FOUND", "No record found for the participant.");
          this.$toastr.error(message);
        });

      if (username.trim() == "") {
        //Do not show any errors
        this.usernameReq.showErrors = false;
        Object.keys(this.usernameReq).forEach(key => (this.usernameReq[key] = false));
      } else if (username.length < 8) {
        this.usernameReq.min8char = false;
      } else if (username.length >= 8) {
        this.usernameReq.min8char = true;
      }
      //No special char at the start and end
      const specialCharStartEndPattern = new RegExp("^[a-zA-Z0-9](.*[a-zA-Z0-9])?$");
      if (specialCharStartEndPattern.test(username)) {
        this.usernameReq.startEndSpecialChar = true;
      } else {
        this.$api.checkUsernameFact({ username: username }).then(res => {
          this.usernameReq.unique = res.data.errorCode == null;
        });
        //this.usernameReq.unique = false;
        if (username.trim() == "") {
          //Do not show any errors
          this.usernameReq.showErrors = false;
        } else if (username.length < 8) {
          this.usernameReq.min8char = false;
        } else if (username.length >= 8) {
          this.usernameReq.min8char = true;
        }
        //No special char at the start and end
        const specialCharStartEndPattern = new RegExp("^[a-zA-Z0-9](.*[a-zA-Z0-9])?$");
        if (specialCharStartEndPattern.test(username)) {
          this.usernameReq.startEndSpecialChar = true;
        } else {
          this.usernameReq.startEndSpecialChar = false;
        }
        //Must contain only allowed chars
        const allowedCharPattern = new RegExp("^[a-zA-Z0-9._@-]*$");
        if (allowedCharPattern.test(username)) {
          this.usernameReq.hasAllowedChar = true;
        } else {
          this.usernameReq.hasAllowedChar = false;
        }
      }
    },

    validPassword(password) {
      if (password.trim() == "") {
        Object.keys(this.passwordReq).forEach(key => (this.passwordReq[key] = false));
      } else {
        this.passwordReq.showErrors = true;
        const numberPattern = new RegExp("^(?=.*[0-9]).+$");
        const specialPattern = new RegExp("^(?=.*[!\"@#$%&'()*+,-./:;<=>?[\\]^_{|}~]).+$");
        const letterPattern = new RegExp("^(?=.*[a-zA-Z]).+$");
        // empty password check
        if (password === "") {
          this.passwordReq.showErrors = false;
        } else if (password.length >= 8) {
          this.passwordReq.min8char = true;
        } else if (password.length < 8) {
          this.passwordReq.min8char = false;
        }
        // any letter pattern check
        if (letterPattern.test(password)) {
          this.passwordReq.anyLetter = true;
        } else {
          this.passwordReq.anyLetter = false;
        }
        //Atleast 1 number
        if (numberPattern.test(password)) {
          this.passwordReq.number = true;
        } else {
          this.passwordReq.number = false;
        }
        //Atleast 1 special char
        if (specialPattern.test(password)) {
          this.passwordReq.specialChar = true;
        } else {
          this.passwordReq.specialChar = false;
        }
      }
    },
    checkIfPasswordMatches() {
      if (this.participantRegistration.password === this.participantRegistration.confirmPassword) {
        this.passwordReq.passwordMatched = true;
      } else {
        this.passwordReq.passwordMatched = false;
      }
    },
    getActivitesData() {
      const studyID = this.portal.getStudy();
      const langID = this.portal.getLanguage();
      const studyDataObj = this.bitrise.getJsonData("study");
      if (studyDataObj) {
        studyDataObj
          .then(response => {
            this.$store.dispatch("setParticipantsPortalStudyData", response);
            nprogress.done();
            this.$router.push({
              name: "participants-portal-activities",
              params: { studyId: studyID, languageId: langID }
            });
          })
          .catch(error => {
            nprogress.done();
            const message = this.widget.translate(error.message, "Data sync failed, please try again after some time.");
            if (message) {
              this.$toastr.error(message);
            }
          });
      }
    },

    ...mapActions(["participantLogout", "participantLogin"]),
    openModal() {
      this.verifyIdentity = true;
    },
    logout() {
      this.$store.dispatch("participantLogout");
    },
    getListOfcohart() {
      if (this.selectedCohort != null) {
        this.cohartId = this.selectedCohort.cohortId;
        this.associatedConsent = this.selectedCohort.associatedConsent;
        if (this.associatedConsent && this.associatedConsent.length > 1) {
          this.participantRegistration.participantMeta.firstName = "";
          this.participantRegistration.participantMeta.lastName = "";
        }
      } else {
        this.consent.getCohartData().then(() => {
          this.cohartId = this.consent && this.consent.listOfCohart && this.consent.listOfCohart[0].cohortId;
          this.associatedConsent = this.consent && this.consent.listOfCohart && this.consent.listOfCohart[0].associatedConsent;
        });
      }
    },
    getFormConfig() {
      this.bitrise
        .getJsonData("study_participantregistrationconfiguration")
        .then(response => {
          this.configFields = response.participantRegistrationConfigDetails;
          if (this.isDatavantTokenizationEnabled && this.defaultCountry && this.defaultCountry.toLowerCase() == "us") {
            this.datavantTokenFields = response.dataVantTokens;
          }
          this.customRegistrationField = this.configFields.filter(item => item.key === "regCustomField1" || item.key === "regCustomField2" || item.key === "regCustomField3");
        })
        .catch(e => {
          const message = this.widget.translate(e.message, "Data sync failed, please try again after some time.");
          if (message) {
            this.$toastr.error(message);
          }
        });
    },
    getFieldObject(key) {
      if (key === "birthDate") {
        const val = this.configFields.find(o => o.key === key || o.key === "dobWithYear" || o.key === "dobWithMonthYear");
        this.dobInputFormat = val.key;
        return val || undefined;
      }
      const object = this.configFields.find(o => o.key === key);
      return object || undefined;
    },
    onCloseModel() {
      this.confirmEmailModal = false;
    },
    submitEmail() {
      const loginPayload = {
        country: this.portal.getCountry(),
        deviceId: uuidv4(),
        email: this.pwebCognitoAuthentication ? this.participantRegistration && this.participantRegistration.cognitoUsername.toLowerCase() : this.participantRegistration.participantMeta.email,
        isUserAlreadyLoggedIn: false,
        language: this.portal.getLanguage(),
        participantLocalTime: moment(date).format(),
        participantLocalTimeOffset: moment(date).format("Z"),
        password: this.participantRegistration.participantMeta.password,
        studyId: this.portal.getStudy(),
        studyVersionId: this.portal.getVersion(),
        updatePartLang: false,
        deviceType: "web"
      };
      const VerifyEmailPayload = {
        emailVerified: false,
        otp: "",
        password: "",
        username: this.participantRegistration.cognitoUsername.toLowerCase()
      };
      const self = this;
      if (!this.isVerifyEmailConfim) {
        swal({
          title: self.widget.translate("email_not_verified", "Please check your email to verify your account before continuing."),
          type: "warning",
          showCancelButton: false,
          cancelButtonColor: "var(--primary)!important",
          cancelButtonText: self.widget.translate("OK_BUTTON_TITLE", "Ok")
        }).then(() => {
          this.isVerifyEmailConfim = true;
          this.showLoader = true;
          if (this.pwebCognitoAuthentication) {
            this.$api.verifyCognitoUserEmail(VerifyEmailPayload).then(res => {
              const result = res.body;
              if (result.message) {
                this.cognitoLogin(loginPayload);
              } else {
                this.showLoader = false;
                swal({
                  title: "Please verify your email.",
                  type: "warning",
                  showCancelButton: false,
                  cancelButtonColor: "var(--primary)!important",
                  cancelButtonText: "Ok"
                }).then(() => {});
              }
            });
          } else {
            this.verifyEmail(loginPayload);
          }
        });
      } else {
        if (this.pwebCognitoAuthentication) {
          this.showLoader = true;
          this.$api.verifyCognitoUserEmail(VerifyEmailPayload).then(res => {
            const result = res.body;
            if (result.message) {
              this.cognitoLogin(loginPayload);
            } else {
              this.showLoader = false;
              swal({
                title: this.widget.translate("label_verify_email", "Please verify your email."),
                type: "warning",
                showCancelButton: false,
                cancelButtonColor: "var(--primary)!important",
                cancelButtonText: "Ok"
              }).then(() => {});
            }
          });
        } else {
          this.verifyEmail(loginPayload);
        }
      }
    },
    onResendVerification() {
      const verificationPayload = {
        studyId: this.portal.getStudy(),
        email: this.participantRegistration.participantMeta.email.toLowerCase(),
        language: this.portal.getLanguage()
      };
      this.$api
        .verifyParticipantEmail(verificationPayload)
        .then(res => {
          const message = this.widget.translate(res.message, "send!");
          if (message) {
            this.$toastr.success(message);
          }
        })
        .catch(err => {
          const message = this.widget.translate(err.message, "Error!");
          if (message) {
            this.$toastr.error(message);
          }
        });
    },
    saveParticipant() {
      if (this.participantRegistration.participantMeta.dob) {
        this.getDobFomatted(this.participantRegistration.participantMeta.dob);
      }
      if (this.isPhoneVerification && !this.participantRegistration.verificationOptInSMS) {
        //show modal
        this.modal.helpText = this.widget.translate("label_registration_verification_phone_opt_in_error", "In order to participate in the study without an email address, you must Opt-in to receive SMS notifications to your Verification Phone Number (Mobile). Do you wish to proceed?");
        this.modal.okBtnTitle = this.widget.translate("YES_TEXT", "Yes");
        this.modal.cancelBtnTitle = this.widget.translate("label_cancel", "Cancel");
        this.showOptInSmsModal = !this.showOptInSmsModal;
        return;
      }
      this.disableNextBtn = true;
      const offset = date.getTimezoneOffset();

      const o = Math.abs(offset);
      const localTimeOffset = (offset < 0 ? "+" : "-") + ("00" + Math.floor(o / 60)).slice(-2) + ":" + ("00" + (o % 60)).slice(-2);
      const form = this.participantRegistration;
      const { password, email, confirmPassword } = form;
      form.activationToken = this.participantDetails.getParticipantActivationToken();
      form.deviceType = "Web";
      form.pid = this.participantDetails.getParticipantID();
      form.studyVersionId = this.portal.getVersion();
      form.participantMeta.email = email;
      form.participantMeta.password = password;
      form.participantMeta.nonMobilePhoneNumber = form.participantMeta.nonMobilePhoneNumber !== "" ? form.participantMeta.nonMobilePhoneNumber : form.participantMeta.phoneNumber;
      form.participantMeta.optInSMS = this.participantRegistration.participantMeta.optInSMS == true;
      form.registrationProcess = this.portal.isOpenEnrollmentStudy() ? "ENROLLMENT" : "INVITATION";
      form.participantLocalOffset = localTimeOffset;
      form.participantLocalTime = this.portal.isManualStudyStart() ? "" : moment(date).format();
      form.deviceId = uuidv4();
      this.validated = true;
      this.passwordReq.validated = true;
      this.formValidated = true;
      this.$validator.validateAll().then(valid => {
        if (this.isPhoneVerification) {
          if (!this.pwebCognitoAuthentication) {
            this.passwordReq.passwordMatched = true;
            delete this.participantRegistration.password;
          }
          delete this.participantRegistration.email;
          delete this.participantRegistration.participantMeta.email;
        } else if (confirmPassword.trim() == "") {
          this.passwordReq.passwordMatched = false;
          this.disableNextBtn = false;
          return;
        }
        if (!this.validTelNumber || !this.passwordReq.passwordMatched || (this.pwebCognitoAuthentication && !this.isUsernameValid)) {
          this.hasFieldError = true;
          this.disableNextBtn = false;
        } else if (valid) {
          if (this.contactPhoneObject && this.contactPhoneObject.number.input.trim() != "") {
            this.participantRegistration.participantMeta.phoneNumber = this.contactPhoneObject ? this.contactPhoneObject.number.significant : this.participantRegistration.participantMeta.phoneNumber;
            this.participantRegistration.participantMeta.countryCode = "+" + (this.contactPhoneObject ? this.contactPhoneObject.country.dialCode : this.participantRegistration.participantMeta.dialCode);
          }
          const language = this.portal.getLanguage();
          const country = this.portal.getCountry();

          if (this.isDatavantTokenizationEnabled && this.defaultCountry && this.defaultCountry.toLowerCase() == "us") {
            //Find it Datavant Consent is present then send its status(Accepted/Declined) in singup payload
            let datavantConsentIndex = _.findIndex(this.consentSignatureAndDisclaimer, ["dataVantConsent", true]);
            if (datavantConsentIndex >= 0) {
              let dataVantConsentSignatureAndDisclaimer = this.consentSignatureAndDisclaimer[datavantConsentIndex];
              this.participantRegistration.datavantConsentStatus = dataVantConsentSignatureAndDisclaimer.signature == "" ? "Declined" : "Accepted";
            }

            this.participantRegistration.datavantToken = this.datavantTokens.prepareTokensMeta(this.participantRegistration, this.datavantTokenFields);
          }
          this.participantRegistration.cognitoUsername = this.participantRegistration.cognitoUsername.toLowerCase();
          this.saveParticipantRegistration(this.participantRegistration);
          if (this.pwebCognitoAuthentication) {
            this.$api
              .participantRegistrationCognito(language, country, this.participantRegistration)
              .then(response => {
                this.accesstoken = response.body.accesstoken;
                this.portal.setLocalStorage("pw-openEnrollEmail", this.participantRegistration.email);
                this.portal.setLocalStorage("participantRegisteredAccessToken", this.accesstoken);
                this.participantId = this.participantDetails.getParticipantID(this.accesstoken);
                this.openModal();
                this.saveConsentData();
              })
              .catch(async error => {
                this.confirmEmailModal = false;
                this.disableNextBtn = false;
                const { message } = error.body;
                const errorMessage = await this.isRegistrationFail(message);
                if (errorMessage) {
                  this.$toastr.error(errorMessage);
                }
              });
          } else {
            delete this.participantRegistration["cognitoUsername"];
            this.$api
              .participantRegistration(language, country, this.participantRegistration)
              .then(response => {
                this.accesstoken = response.body.accesstoken;
                this.participantId = this.participantDetails.getParticipantID(this.accesstoken);
                this.openModal();
                this.saveConsentData();
              })
              .catch(async error => {
                this.disableNextBtn = false;
                this.confirmEmailModal = false;
                const { message } = error.body;
                const errorMessage = await this.isRegistrationFail(message);
                if (errorMessage) {
                  this.$toastr.error(errorMessage);
                }
              });
          }
        } else {
          this.disableNextBtn = false;
        }
      });
    },
    isRegistrationFail(message) {
      let errorMessage = "";
      switch (message) {
        case "activation_time_lapsed":
          errorMessage = this.widget.translate(message, "Study activation code has already been used. Please contact the study team to receive a new code.");
          break;
        case "UNABLE_TO_SIGN_IN":
          errorMessage = this.widget.translate(message, "unable to sign in please contact admin.");
          break;
        case "EMAIL_IS_NOT_INVITED":
          errorMessage = this.widget.translate(message, "This email is not invited in our system.");
          break;
        case "INVALID_EMAIL":
          errorMessage = this.widget.translate(message, "Invalid email.");
          break;
        default:
          errorMessage = this.widget.translate(message, message);
          break;
      }
      return errorMessage;
    },
    saveConsentData() {
      Promise.all([this.saveEulaAgrement(), this.saveCohart(), this.saveSignatureAndDiclamer()])
        .then(() => {})
        .catch(err => {
          this.$toastr.error(err);
        });
      if (this.study.consentEnabled) {
        this.saveOnBoardingConsent()
          .then(() => {})
          .catch(err => this.$toastr.error(err));
      }
    },
    confirmParticipant() {
      const pid = this.participantDetails.getParticipantID(this.accesstoken);
      const studyId = this.portal.getStudy();
      const now = new Date();
      const payload = {
        confirmedOnLocal: moment(now).format("YYYY-MM-DDTHH:mm:ss.SSSZZ"),
        confirmedTimeOffset: moment(now).format("Z"),
        isConfirmed: true,
        timezone: moment.tz.guess()
      };
      this.$api
        .confirmParticipant(pid, studyId, payload)
        .then(() => {})
        .catch(err => {
          const message = this.widget.translate(err.message, "Problem in confirming study details.");
          this.$toastr.error(message);
        });
    },
    async saveSignatureAndDiclamer() {
      const pid = this.participantDetails.getParticipantID(this.accesstoken);
      const studyName = window.localStorage.getItem("pw-studyName");
      const studyId = this.portal.getStudy();

      //Find it Datavant Consent is present
      let datavantConsentIndex = _.findIndex(this.consentSignatureAndDisclaimer, ["dataVantConsent", true]);
      if (datavantConsentIndex >= 0) {
        let dataVantConsentSignatureAndDisclaimer = this.consentSignatureAndDisclaimer[datavantConsentIndex];
        this.$delete(this.consentSignatureAndDisclaimer, datavantConsentIndex);
        const consents = {
          consents: [dataVantConsentSignatureAndDisclaimer],
          studyId: studyId,
          studyName: studyName
        };
        let status = dataVantConsentSignatureAndDisclaimer.signature == "" ? "Declined" : "Accepted";
        await this.$api.saveDatavantSignatureAndDisclamer({ pid: pid, status: status }, consents);
      }

      //Normal consent Flow
      const consents = {
        consents: this.consentSignatureAndDisclaimer,
        studyId: studyId,
        studyName: studyName
      };
      await this.$api.saveSignatureAndDisclamer(pid, consents);
    },
    async saveEulaAgrement() {
      const { firstName, lastName } = this.participantRegistration.participantMeta;
      const pid = this.participantDetails.getParticipantID(this.accesstoken);
      const studyName = window.localStorage.getItem("pw-studyName");
      const eulaPayload = {
        eulaStatus: true,
        firstName: firstName,
        lastName: lastName,
        studyId: this.portal.getStudy(),
        studyName: studyName
      };
      await this.$api.participantEulaAgrement(pid, eulaPayload);
    },

    async saveCohart() {
      if (this.cohartId) {
        const cohartPayload = {
          cohortId: this.cohartId,
          participantId: this.participantDetails.getParticipantID(this.accesstoken),
          studyId: this.portal.getStudy()
        };
        await this.$api.participantSaveCohort(cohartPayload);
      }
    },

    async saveOnBoardingConsent() {
      const latestConsentId = this.associatedConsent.pop().consentId;
      const nowDate = new Date();
      const onBoaringPayload = {
        onBoardingConsentData: [
          {
            cohortId: this.cohartId,
            consentEndTime: moment(nowDate).format(),
            consentId: latestConsentId,
            consentStartTime: moment(nowDate).format(),
            id: "",
            studyVersionId: this.portal.getVersion()
          }
        ],
        participantLocalTime: moment(nowDate).format()
      };
      const studyId = this.portal.getStudy();
      const pid = this.participantDetails.getParticipantID(this.accesstoken);
      await this.$api.participantSaveConsents(pid, studyId, onBoaringPayload);
    },
    makeSameAsVerificationPhone() {
      if (this.isSameAsVerificationPhone) {
        this.contactPhoneObject = Object.assign({}, this.phoneObject);
        this.participantRegistration.participantMeta.phoneNumber = this.contactPhoneObject.number.significant;
        this.participantRegistration.participantMeta.countryCode = "+" + this.contactPhoneObject.country.dialCode;
      } else {
        this.participantRegistration.participantMeta.phoneNumber = "";
      }
    },
    getGenderType(gender) {
      return gender != "Other gender identity" ? gender.toLowerCase() : "SERVER_OTHER_GENDER";
    }
  },
  directives: {
    clickoutside: {
      bind: function(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          if (!(el == event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind: function(el) {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
      stopProp(event) {
        event.stopPropagation();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.ckeckInput {
  pointer-events: none;
}
.study-info-container {
  .regex-req {
    font-size: 12px;
    margin-top: 10px;
    .req-title {
      font-family: Roboto-Bold;
      color: #959fa4;
    }
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      color: #b0bac9;
      li {
        margin: 0;
        span {
          margin-right: 8px;
          display: inline-block;
          vertical-align: middle;
          &.initial-state {
            border: 2px solid;
            min-width: 13px;
            min-height: 13px;
          }
          &.success {
            background: url("/static/img/participant-web-img/success-icon.svg") no-repeat center;
            min-height: 15px;
            min-width: 15px;
            border: 0;
          }
          &.error {
            background: url("/static/img/participant-web-img/error-icon.svg") no-repeat center;
            min-height: 15px;
            min-width: 15px;
            border: 0;
          }
        }

        @keyframes spinner-border {
          to {
            transform: rotate(360deg);
          }
        }

        .spinner-border {
          display: inline-block;
          width: 13px;
          height: 13px;
          vertical-align: text-bottom;
          border: 0.2rem solid currentColor;
          border-right-color: transparent;
          // stylelint-disable-next-line property-blacklist
          border-radius: 50% !important;
          animation: spinner-border 0.75s linear infinite;
        }

        .spinner-border-sm {
          width: 13px;
          height: 13px;
          border-width: 0.2rem;
        }
      }
    }
  }
}
.close-icon {
  opacity: 0.5;
  cursor: pointer;
}
.partipant-registration-modal__container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  .import-success-header-text {
    font-weight: bold;
    font-size: 24px;
    line-height: 1.33;
  }
  .btn-submit {
    max-width: 78% !important;
  }
}
.btn-submit {
  max-width: 78% !important;
}
.error-style {
  position: absolute;
  font-size: 12px;
  top: 53px;
  color: #ff4848;
  padding-bottom: 5px;
}
.phone-error-style {
  left: 138px !important;
  position: relative;
  top: -10px;
}
.calenderIcon {
  margin-right: 2%;
}
#profile-tab .form-group {
  margin: 24px 0 0 !important;
  opacity: 1;
}
.form-align-calender {
  display: flex;
  align-items: center;
  opacity: 1;
}
.study-info-container.profile-info-details {
  padding: 40px 42px 48px 46px;
  height: auto !important;
}
.profile-registration-dropdown.form-group label {
  top: -8px !important;
  left: 16px !important;
  padding: 0 5px !important;
  font-size: 12px !important;
  letter-spacing: 0.4px;
  text-align: left;
  color: #888888;
  background-color: #ffffff;
}

.form-group.Re-enter_Pswrd {
  background-color: #ffffff;
}
.Re-enter_Pswrd .form-control {
  background-color: #ffffff;
}
.regex-req .validation-list {
  margin-left: 15px !important;
}
.prof-info-title {
  font-family: Roboto;
  font-size: 20px;
  line-height: 1.6;
  text-align: left;
  color: #000000;
  cursor: context-menu;
}
.form-check-phone {
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: 0.15px;
  text-align: left;
  color: rgba(0, 0, 0, 0.6);
}
.profile-info-page1 .form-group label {
  background-color: transparent;
  padding: 2px 7px !important;
}
.paricipant-reg-name {
  font-family: Roboto;
  font-size: 13px;
  line-height: 1.38;
  letter-spacing: -0.08px;
  text-align: center;
  color: #83c6a8;
}
.flag-img {
  max-height: 30px;
  max-width: 30px;
}
.floating-select:focus ~ .label,
.floating-select:not([value=""]):valid ~ .label {
  top: -10px !important;
  left: 16px !important;
  padding: 0 5px !important;
  font-size: 12px !important;
  letter-spacing: 0.4px !important;
  text-align: left;
  color: #888888;
  background-color: #ffffff !important;
}
</style>
<style>
.d-header-none .modal-header,
.d-header-none .modal-footer {
  display: none !important;
}
.participant-gender-modal .form-control {
  font-size: 18px !important;
}
.participant-gender-modal .form-control::placeholder {
  text-align: center;
}
.reg-change-email-modal .partipant-registration-modal__container {
  display: block;
  align-items: unset;
}
.reg-modal-change-email {
  position: relative;
}
.participant-gender-modal .reg-modal-change-email label {
  position: absolute;
  top: 66px;
  left: 73px;
  font-size: 16px;
}
.participant-gender-modal .reg-modal-change-email input:focus ~ label,
.participant-gender-modal .reg-modal-change-email input:valid ~ label {
  position: absolute;
  top: 40px !important;
  padding: 0 10px;
  left: 78px !important;
  font-size: 14px !important;
  color: #888888 !important;
}
</style>
